/*
 * Copyright (c) 2021. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import SendApplication from "./SendApplication";
import { Helmet } from "react-helmet-async";
import LayoutPublicRoutes from "@/src/containers/Layouts/LayoutPublicRoutes";
import JobPageMeta from "@/src/containers/Pages/Generic/JobsPage/JobPageMeta";
import DoAndDont from "./DoAndDont";
import { refaDosAndDonts } from "@/src/services/jobs/jobsService";
import { Box } from "@mui/material";
import AreYouInterestedSection from "./AreYouInterestedSection";
import { LAW_FIRM_ADDRESS, LAW_FIRM_ADDRESS_LOCALITY, LAW_FIRM_ADDRESS_REGION, LAW_FIRM_POSTAL_CODE } from "@/src/services/globalVars";
import { gaSet } from "@/src/services/googleAnalyticsService";
import JobContainer, { JobContainerProps } from "@/src/components/Jobs/Job/JobContainer";
import jobStyles from "@/src/components/Jobs/Job/jobStyles";

const serviceRafaJobDetails: JobContainerProps = {
  title: "Rechtsanwaltsfachangestellte/r Vollzeit (m/w/d)",
  hardFacts: [
    {
      title: "Standort",
      value: "Köln, DE",
    },
    {
      title: "Beschäftigungsart",
      value: "Vollzeit",
    },
    {
      title: "Arbeitsmodell",
      value: "Hybrid",
    },
    {
      title: "Bereich",
      value: "Operations",
    },
  ],
  content: [
    {
      type: "headline",
      headline: "Du bist Rechtsanwaltsfachangestellte (m/w/d)? Warum eine Legal-Tech-Kanzlei dein nächster Karriereschritt sein sollte!",
    },
    {
      type: "text",
      text:
        "Du bist es leid, vom obligatorischen Obstkorb und halbjährlich stattfindenden Kanzleifeiern für einen Job “überzeugt” zu werden? Du willst gerne " +
        "nachmittags deinem Hobby nachgehen oder dein Kind von der Schule oder KiTa abholen, anstatt jeden Tag von 9 Uhr bis 18 Uhr in einem kahlen Büro zu " +
        "sitzen?",
    },
    {
      type: "text",
      text:
        "Wir bieten dir eine aufregende Arbeitsumgebung für Rechtsanwaltsfachangestellte. Im Gegensatz zu traditionellen Kanzleien setzen wir als " +
        "Legal-Tech-Kanzlei auf innovative Technologien, die die Effizienz und Effektivität von Rechtsdienstleistungen verbessern. Als Teil des Teams kannst du " +
        "unsere eigenen Technologien nutzen und an der Entwicklung mitarbeiten oder dich einfach auf deine Fähigkeiten als Rechtsanwaltsfachangestellte " +
        "konzentrieren, ohne zeitraubende und mühsame Aufgaben erledigen zu müssen.",
    },
    {
      type: "text",
      text:
        "Lerne Legalbird kennen und sei dabei, wenn wir nicht nur den Rechtsmarkt, sondern auch die Arbeitsbedingungen für jeden in unserem Team " +
        "revolutionieren!",
    },
    {
      type: "headline",
      headline: " DOs and DON'Ts - Was uns bei Legalbird von klassischen Kanzleien unterscheidet",
    },
  ],
  afterContent: (
    <>
      {refaDosAndDonts.map((refaDoAndDont) => (
        <Box sx={jobStyles.text}>
          <DoAndDont
            doHeadline={refaDoAndDont.doHeadline}
            doText={refaDoAndDont.doText}
            dontHeadline={refaDoAndDont.dontHeadline}
            dontText={refaDoAndDont.dontText}
          />
        </Box>
      ))}
      <AreYouInterestedSection role={"Rechtsanwaltsfachangestellte:r"} />
      <SendApplication />
    </>
  ),
};

const ServiceRAFA = () => {
  const title = "Jobs - Rechtsanwaltsfachangestellte/r | Legalbird";

  gaSet({ title: title });

  return (
    <LayoutPublicRoutes>
      <JobPageMeta />
      <Helmet>
        <title>{title}</title>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "JobPosting",
            title: "Rechtsanwaltsfachangestellte in Vollzeit (m/w/d)",
            description:
              "          <h3>Legalbird Rechtsanwälte </h3>" +
              "            <p>Wir helfen unseren Mandanten, ihre rechtlichen Fragestellungen " +
              "               sicher und schnell online zu lösen. Sie schätzen eine offene Atmosphäre, in der auf Augenhöhe zusammengearbeitet wird? " +
              "               Dann bewerben Sie sich gerne bei uns!</p>",
            responsibilities:
              "          <h3>Was Ihre Aufgaben sein werden</h3>" +
              "          <ul>" +
              "            <li>Sie unterstützen unsere Anwälte administrativ in deren Mandaten sowie in der Korrespondenz mit den Kunden</li>" +
              "            <li>Sie verwalten den Postprozess und arbeiten gemeinsam mit unseren Partneranwälten an der stetigen Optimierung unserer Prozesse</li>" +
              "          </ul>",
            qualifications:
              "          <h3>Was Sie dazu brauchen</h3>" +
              "          <ul>" +
              "            <li>Sie haben Ihre Ausbildung zur Rechtsanwaltsfachangestellten erfolgreich abgeschlossen und besitzen Berufserfahrung</li>" +
              "            <li>Sie haben Erfahrung mit der telefonischen Betreuung von Mandanten, dem Postprozess und der Korrespondenz mit Gerichten</li>" +
              "            <li>Sie arbeiten gerne und sehr sicher mit dem PC</li>" +
              "          </ul>",
            jobBenefits:
              "           <h3>Was wir Ihnen bieten und was uns auszeichnet</h3>" +
              "           <ul>" +
              "           <li>Eine gute Bezahlung und Arbeiten auf Augenhöhe in einem motivierten und erfahrenen Team</li>" +
              "           <li>Eine tolle Arbeitsatmosphäre in einem modernen Büro inkl. Außenterrasse mit einem phantastischen Blick auf den Rhein </li>" +
              "           <li>Sehr gute Erreichbarkeit mit öffentlichen Verkehrsmitteln (Jobticket inklusive)</li>" +
              "           </ul>",
            identifier: {
              "@type": "PropertyValue",
              name: "Legalbird",
              value: "01072021",
            },
            datePosted: "2021-07-01",
            validThrough: "2021-12-31T00:00",
            industry: "Rechtsdienstleistungen",
            employmentType: "Vollzeit",
            hiringOrganization: {
              "@type": "Organization",
              name: "Legalbird",
              sameAs: "https://www.legalbird.de",
              logo: "https://www.legalbird.de/img/legalbird-logo.png",
            },
            jobLocation: {
              "@type": "Place",
              address: {
                "@type": "PostalAddress",
                streetAddress: LAW_FIRM_ADDRESS,
                addressLocality: LAW_FIRM_ADDRESS_LOCALITY,
                addressRegion: LAW_FIRM_ADDRESS_REGION,
                postalCode: LAW_FIRM_POSTAL_CODE,
                addressCountry: "DE",
              },
            },
          })}
        </script>
      </Helmet>
      <JobContainer {...serviceRafaJobDetails} />
    </LayoutPublicRoutes>
  );
};

export default ServiceRAFA;
